import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import { Card, Button, Input, Badge, Avatar, Modal, Textarea, Select } from '../components/ui';
import { 
  Search, 
  MapPin, 
  Star, 
  Instagram, 
  Globe, 
  Plus,
  Loader2,
  Filter,
  Image,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Artists = () => {
  const { t } = useTranslation();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStyle, setFilterStyle] = useState('');
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    studio_name: '',
    location: '',
    instagram: '',
    website: '',
    styles: [],
  });

  const [reviewData, setReviewData] = useState({
    rating: 5,
    comment: '',
  });

  const styles = [
    'Blackwork', 'Geometric', 'Traditional', 'Neo Traditional', 
    'Realistic', 'Watercolor', 'Minimalist', 'Japanese', 'Tribal',
    'Fine Line', 'Dotwork', 'Script', 'Abstract'
  ];

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      let url = `${API_URL}/api/artists`;
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterStyle) params.append('style', filterStyle);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setArtists(data);
      }
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArtistDetails = async (artistId) => {
    try {
      const response = await fetch(`${API_URL}/api/artists/${artistId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedArtist(data);
      }
    } catch (error) {
      console.error('Failed to fetch artist details:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchArtists();
  };

  const handleAddArtist = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/artists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        fetchArtists();
        setShowAddModal(false);
        setFormData({
          name: '',
          studio_name: '',
          location: '',
          instagram: '',
          website: '',
          styles: [],
        });
      }
    } catch (error) {
      console.error('Failed to add artist:', error);
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!selectedArtist) return;

    try {
      const response = await fetch(`${API_URL}/api/artists/${selectedArtist.artist_id}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(reviewData),
      });

      if (response.ok) {
        fetchArtistDetails(selectedArtist.artist_id);
        setShowReviewModal(false);
        setReviewData({ rating: 5, comment: '' });
      }
    } catch (error) {
      console.error('Failed to add review:', error);
    }
  };

  const toggleStyle = (style) => {
    setFormData(prev => ({
      ...prev,
      styles: prev.styles.includes(style)
        ? prev.styles.filter(s => s !== style)
        : [...prev.styles, style]
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl mb-1" data-testid="artists-title">
              {t('artists.title', 'Artist')} <span className="text-primary">{t('artists.guide', 'Guide')}</span>
            </h1>
            <p className="text-muted-foreground">
              {t('artists.subtitle', 'Discover the best tattoo artists')}
            </p>
          </div>
          <Button onClick={() => setShowAddModal(true)} data-testid="add-artist-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('artists.add', 'Add Artist')}
          </Button>
        </div>

        {/* Search & Filter */}
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('artists.searchPlaceholder', 'Search artist or studio...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12"
              data-testid="artist-search-input"
            />
          </div>
          <Select
            value={filterStyle}
            onChange={(e) => setFilterStyle(e.target.value)}
            className="sm:w-48"
            data-testid="style-filter"
          >
            <option value="">{t('artists.allStyles', 'All Styles')}</option>
            {styles.map(style => (
              <option key={style} value={style}>{style}</option>
            ))}
          </Select>
          <Button type="submit" variant="outline">
            <Filter className="w-4 h-4 mr-2" />
            {t('artists.filterBtn', 'Filter')}
          </Button>
        </form>

        {/* Artists Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          </div>
        ) : artists.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {artists.map((artist, index) => (
              <motion.div
                key={artist.artist_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className="overflow-hidden cursor-pointer hover:border-primary/50"
                  onClick={() => fetchArtistDetails(artist.artist_id)}
                  data-testid={`artist-card-${index}`}
                >
                  {/* Portfolio Preview */}
                  <div className="h-40 bg-muted relative">
                    {artist.portfolio_images?.length > 0 ? (
                      <div className="grid grid-cols-2 h-full">
                        {artist.portfolio_images.slice(0, 2).map((img, i) => (
                          <img
                            key={i}
                            src={img}
                            alt={`${artist.name} portfolio`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-background to-muted">
                        {artist.profile_image ? (
                          <img src={artist.profile_image} alt={artist.name} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center">
                            <span className="font-heading text-2xl text-primary">{artist.name?.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-heading text-lg">{artist.name}</h3>
                        {artist.studio_name && (
                          <p className="text-sm text-muted-foreground">{artist.studio_name}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-primary">
                        <Star className="w-4 h-4 fill-current" />
                        <span className="text-sm font-medium">{(artist.rating ?? 0).toFixed(1)}</span>
                      </div>
                    </div>

                    {artist.location && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <MapPin className="w-4 h-4" />
                        {artist.location}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1">
                      {artist.styles?.slice(0, 3).map((style, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {style}
                        </Badge>
                      ))}
                      {artist.styles?.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{artist.styles.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-xl mb-2">{t('artists.notFound', 'No Artists Found')}</h3>
            <p className="text-muted-foreground mb-6">
              {t('artists.notFoundDesc', 'Try different search criteria or add a new artist.')}
            </p>
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('artists.add', 'Add Artist')}
            </Button>
          </Card>
        )}

        {/* Artist Detail Modal */}
        {selectedArtist && (
          <Modal
            isOpen={!!selectedArtist}
            onClose={() => setSelectedArtist(null)}
            title={selectedArtist.name}
          >
            <div className="space-y-6">
              {/* Artist Info */}
              <div className="flex items-start gap-4">
                <Avatar
                  src={selectedArtist.profile_image || selectedArtist.portfolio_images?.[0]}
                  alt={selectedArtist.name}
                  fallback={selectedArtist.name.charAt(0)}
                  size="xl"
                />
                <div className="flex-1">
                  {selectedArtist.studio_name && (
                    <p className="text-muted-foreground">{selectedArtist.studio_name}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center gap-1 text-primary">
                      <Star className="w-5 h-5 fill-current" />
                      <span className="font-heading text-lg">{(selectedArtist.rating ?? 0).toFixed(1)}</span>
                      <span className="text-sm text-muted-foreground">
                        ({selectedArtist.review_count || 0} {t('artists.reviews', 'reviews')})
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="flex flex-wrap gap-3">
                {selectedArtist.location && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {selectedArtist.location}
                  </Badge>
                )}
                {selectedArtist.instagram && (
                  <a 
                    href={`https://instagram.com/${selectedArtist.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Badge variant="secondary" className="flex items-center gap-1 hover:bg-primary/20">
                      <Instagram className="w-3 h-3" />
                      {selectedArtist.instagram}
                    </Badge>
                  </a>
                )}
                {selectedArtist.website && (
                  <a href={selectedArtist.website} target="_blank" rel="noopener noreferrer">
                    <Badge variant="secondary" className="flex items-center gap-1 hover:bg-primary/20">
                      <Globe className="w-3 h-3" />
                      Website
                    </Badge>
                  </a>
                )}
              </div>

              {/* Styles */}
              <div>
                <h4 className="text-sm font-medium mb-2">{t('artists.styles', 'Styles')}</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedArtist.styles?.map((style, i) => (
                    <Badge key={i}>{style}</Badge>
                  ))}
                </div>
              </div>

              {/* Reviews */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium">{t('artists.reviews', 'Reviews')}</h4>
                  <Button size="sm" variant="outline" onClick={() => setShowReviewModal(true)}>
                    {t('artists.review', 'Review')}
                  </Button>
                </div>
                {selectedArtist.reviews?.length > 0 ? (
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {selectedArtist.reviews.map((review, i) => (
                      <div key={i} className="p-3 bg-muted rounded-sm">
                        <div className="flex items-center gap-2 mb-1">
                          {[...Array(5)].map((_, j) => (
                            <Star 
                              key={j} 
                              className={`w-3 h-3 ${j < review.rating ? 'text-primary fill-current' : 'text-muted-foreground'}`} 
                            />
                          ))}
                          <span className="text-xs text-muted-foreground">
                            {review.user_name && <span className="font-medium mr-2">{review.user_name}</span>}
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('artists.noReviews', 'No reviews yet.')}</p>
                )}
              </div>
            </div>
          </Modal>
        )}

        {/* Add Artist Modal */}
        <Modal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          title={t('artists.add', 'Add Artist')}
        >
          <form onSubmit={handleAddArtist} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Sanatçı Adı *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Sanatçı adı"
                required
                data-testid="artist-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Stüdyo Adı</label>
              <Input
                value={formData.studio_name}
                onChange={(e) => setFormData({...formData, studio_name: e.target.value})}
                placeholder="Stüdyo adı"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Konum</label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="Şehir, Ülke"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Instagram</label>
                <Input
                  value={formData.instagram}
                  onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                  placeholder="@kullanici"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Website</label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Stiller</label>
              <div className="flex flex-wrap gap-2">
                {styles.map(style => (
                  <button
                    key={style}
                    type="button"
                    onClick={() => toggleStyle(style)}
                    className={`px-3 py-1 text-sm rounded-sm border transition-colors ${
                      formData.styles.includes(style)
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {style}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>
                İptal
              </Button>
              <Button type="submit" className="flex-1" data-testid="save-artist-btn">
                Kaydet
              </Button>
            </div>
          </form>
        </Modal>

        {/* Add Review Modal */}
        <Modal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          title="Değerlendirme Ekle"
        >
          <form onSubmit={handleAddReview} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Puan</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setReviewData({...reviewData, rating})}
                    className="p-2"
                  >
                    <Star 
                      className={`w-8 h-8 transition-colors ${
                        rating <= reviewData.rating ? 'text-primary fill-current' : 'text-muted-foreground'
                      }`} 
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Yorum *</label>
              <Textarea
                value={reviewData.comment}
                onChange={(e) => setReviewData({...reviewData, comment: e.target.value})}
                placeholder="Deneyiminizi paylaşın..."
                rows={4}
                required
                data-testid="review-comment-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowReviewModal(false)}>
                İptal
              </Button>
              <Button type="submit" className="flex-1" data-testid="save-review-btn">
                Gönder
              </Button>
            </div>
          </form>
        </Modal>
      </main>
    </div>
  );
};

export default Artists;
